import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import UnderlineTabNav from '@/components/UnderlineTabNav'
import WarehouseBrightSitesLedgerSync from '@/components/WarehouseBrightSitesLedgerSync'
import WarehouseInventoryFlow from '@/components/WarehouseInventoryFlow'
import { coerceLedgerMoneyFields } from '@/components/ledgerMoney'
import WarehouseLedgerTable, { type EditableLedgerRow } from '@/components/WarehouseLedgerTable'
import WarehouseSkuCrosswalkCard from '@/components/WarehouseSkuCrosswalkCard'
import WarehouseVendorsCard from '@/components/WarehouseVendorsCard'
import {
  countLedgerRowsWithRedStatus,
  ledgerProductLabel,
  normalizeLedgerKind,
  productForSku,
} from '@/components/ledgerFilters'
import {
  isLedgerRowDirtyAgainstBaseline,
  isSaveableDirtyLedgerRow as isSaveableDirtyLedgerTxnRow,
  ledgerRowToPatch,
  snapshotLedgerEditableFields,
  type LedgerEditableSnapshot,
} from '@/components/ledgerRowDirty'
import {
  getWarehouseLedgerCatalogAsOf,
  listWarehouseLedger,
  patchWarehouseLedgerTransaction,
  unvoidWarehouseLedgerTransaction,
  voidWarehouseLedgerTransaction,
} from '@/services/warehouse'
import { useWarehouseProblemStore } from '@/store/warehouseProblemStore'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'
import type { CatalogStockRow } from '@/utils/ledgerFlow'
import { normalizeBulkSku } from '@/utils/normalizeBulkSku'
import type {
  WarehouseBkMatchItem,
  WarehouseInventoryLayerItem,
  WarehouseInventoryManualEntryItem,
  WarehouseInventoryMovementItem,
  WarehouseInvoiceSummary,
  WarehouseLedgerCatalogAsOfItem,
  WarehouseSkuCrosswalkItem,
} from '@/services/warehouseTypes'

export type InventoryViewMode = 'ledger' | 'flow' | 'catalog' | 'vendors'

export function normalizeInventoryViewMode(
  stored: string | null | undefined
): InventoryViewMode {
  if (stored === 'ledger' || stored === 'catalog' || stored === 'flow' || stored === 'vendors') {
    return stored
  }
  if (stored === 'progression') return 'ledger'
  return 'ledger'
}

export type StockBySkuEntry = {
  sku: string
  product: string
  casesLeft: number
  avgCost: number | null
}

export type ProgressionRowKind = 'opening' | 'manufacturer' | 'shipment' | 'adjustment' | 'return'

/** How the Progression row was created. */
export type ProgressionRowMethod = 'manual' | 'product_invoice' | 'shipment'

export type ProgressionRow = {
  key: string
  /** Bookkeeper ledger row id (movement, manual entry, or layer). */
  ledgerTxnId: number | null
  /** Manual entry or movement id suitable for Bright Sites linking; null for layer-only rows. */
  linkableLedgerTxnId: number | null
  linkableSource: 'manual' | 'movement' | 'layer' | null
  /** Bright Sites inventory log id when linked. */
  bsLogId: number | null
  /** Bright Sites / warehouse order number when available. */
  orderNumber: string | null
  kind: ProgressionRowKind
  method: ProgressionRowMethod
  date: string
  sku: string
  product: string
  invoiceNumber: string
  /** True when invoice comes from a linked source and should stay read-only. */
  invoiceLinked: boolean
  cases: number
  unitCost: number | null
  destination: string | null
  detail: string
  problem: string | null
  warnings: string[]
}

type StockRow = {
  sku: string
  product: string
  casesLeft: number
  avgCost: number
}

type WarehouseInventoryWorkspaceProps = {
  availableMonths: string[]
  formatMonthLabel: (month: string) => string
  layers: WarehouseInventoryLayerItem[]
  movements: WarehouseInventoryMovementItem[]
  invoices: WarehouseInvoiceSummary[]
  bkMatches: WarehouseBkMatchItem[]
  skuDisplayNames: Map<string, string>
  manualEntries: WarehouseInventoryManualEntryItem[]
  pendingKeys?: Set<string>
  viewMode: InventoryViewMode
  onViewModeChange: (mode: InventoryViewMode) => void
  catalogSkuFilterRequest?: { sku: string; nonce: number } | null
  ledgerBkTxnFilterRequest?: { bkTxnId: string; nonce: number } | null
  onConfirmPending?: (keys: string[]) => void
  onSkuCrosswalkUpdated?: (rows: WarehouseSkuCrosswalkItem[]) => void
  onSkuCrosswalkSaved?: (rows: WarehouseSkuCrosswalkItem[]) => void
  busy?: boolean
  brightSitesAuthRecoveryHint?: boolean
  onBrightSitesAuthError?: () => void
  onBrightSitesApplied?: () => void | Promise<void>
  onJumpToProductInvoice?: (productInvoiceLineId: number) => void
}

/** Destination shown for warehouse-side events (opening, manufacturer restock, adjustments). */
export const PROGRESSION_WAREHOUSE_DESTINATION = 'Warehouse'

const STORE_DESTINATIONS = ['Highland Park', 'River Oaks'] as const

function methodFromKind(kind: ProgressionRowKind): ProgressionRowMethod {
  if (kind === 'shipment') return 'shipment'
  if (kind === 'manufacturer') return 'product_invoice'
  return 'manual'
}

function destinationForKind(
  kind: ProgressionRowKind,
  existingDestination?: string | null
): string {
  if (kind === 'shipment') {
    const trimmed = existingDestination?.trim()
    if (trimmed && trimmed !== PROGRESSION_WAREHOUSE_DESTINATION) return trimmed
    return STORE_DESTINATIONS[0]
  }
  const trimmed = existingDestination?.trim()
  if (trimmed) return trimmed
  return PROGRESSION_WAREHOUSE_DESTINATION
}

function layerKind(layer: WarehouseInventoryLayerItem): ProgressionRowKind {
  const source = (layer.source_type ?? '').toLowerCase()
  if (source === 'store_seed' || layer.bk_order_number === 'SEED') return 'opening'
  if (source === 'manual_adjustment' || layer.bk_order_number === 'ADJ') return 'adjustment'
  return 'manufacturer'
}

function layerOrderNumber(layer: WarehouseInventoryLayerItem): string | null {
  const order = (layer.bk_order_number ?? '').trim()
  if (!order || order === 'SEED' || order === 'ADJ') return null
  return order
}

function layerProgressionKey(layer: WarehouseInventoryLayerItem): string {
  return `layer:${layer.layer_key}`
}

function movementProgressionKey(move: WarehouseInventoryMovementItem): string {
  return `move:${move.move_key}`
}

export function manualProgressionKey(entry: WarehouseInventoryManualEntryItem): string {
  const cases = Number(entry.cases) || 0
  return `manual:${entry.id ?? `${entry.entry_date}:${entry.bk_sku}:${cases}`}`
}

export function findManualEntryIndexForProgressionKey(
  entries: WarehouseInventoryManualEntryItem[],
  rowKey: string
): number {
  if (!rowKey.startsWith('manual:')) return -1
  const suffix = rowKey.slice('manual:'.length)
  const numericId = Number(suffix)
  if (suffix !== '' && Number.isFinite(numericId) && !suffix.includes(':')) {
    const byId = entries.findIndex((entry) => entry.id === numericId)
    if (byId >= 0) return byId
  }
  return entries.findIndex((entry) => {
    const cases = Number(entry.cases) || 0
    return `${entry.entry_date}:${entry.bk_sku}:${cases}` === suffix
  })
}

export function isSaveableDirtyLedgerRow(row: {
  dirty: boolean
  sku: string
  kind: string
  cases: number
}): boolean {
  return (
    row.dirty &&
    row.sku.trim() !== '' &&
    (row.kind === 'opening' || (row.kind === 'adjustment' && row.cases !== 0))
  )
}

function invoiceForBkOrder(
  orderNumber: string,
  bkMatches: WarehouseBkMatchItem[]
): string {
  const match = bkMatches.find(
    (item) =>
      (item.order_number ?? '').trim() === orderNumber.trim() &&
      Boolean(item.invoice_number?.trim())
  )
  return match?.invoice_number?.trim() ?? ''
}

function whInvoiceForMonth(
  reportMonth: string,
  invoices: WarehouseInvoiceSummary[]
): string {
  const normalized = reportMonth.trim()
  const match = invoices.find((invoice) => {
    const type = (invoice.invoice_type ?? '').toLowerCase()
    if (type !== 'wh_service' && type !== 'mixed') return false
    return (invoice.report_month ?? '').trim() === normalized
  })
  return match?.invoice_number?.trim() ?? ''
}

export function buildProgressionRows(
  layers: WarehouseInventoryLayerItem[],
  movements: WarehouseInventoryMovementItem[],
  skuDisplayNames: Map<string, string>,
  manualEntries: WarehouseInventoryManualEntryItem[] = [],
  bkMatches: WarehouseBkMatchItem[] = [],
  invoices: WarehouseInvoiceSummary[] = []
): ProgressionRow[] {
  const rows: ProgressionRow[] = []
  const manualByLayerKey = new Map(
    manualEntries
      .filter((entry) => entry.layer_key)
      .map((entry) => [entry.layer_key as string, entry])
  )

  for (const layer of layers) {
    const qty = Number(layer.qty_received) || 0
    if (qty === 0) continue
    const kind = layerKind(layer)
    const warnings: string[] = []
    const unitCost = Number(layer.unit_cost)
    if (!Number.isFinite(unitCost) || unitCost <= 0) {
      warnings.push('Missing or zero cost per case')
    }
    const manual = manualByLayerKey.get(layer.layer_key)
    let invoiceNumber = ''
    let invoiceLinked = false
    if (kind === 'manufacturer') {
      invoiceNumber = invoiceForBkOrder(layer.bk_order_number, bkMatches)
      invoiceLinked = Boolean(invoiceNumber)
    } else if (manual?.cost_source_invoice?.trim()) {
      invoiceNumber = manual.cost_source_invoice.trim()
    }
    rows.push({
      key: layerProgressionKey(layer),
      ledgerTxnId: manual?.id ?? layer.id ?? null,
      linkableLedgerTxnId: manual?.id ?? layer.id ?? null,
      linkableSource: manual?.id != null ? 'manual' : layer.id != null ? 'layer' : null,
      bsLogId: manual?.bs_log_id ?? layer.bs_log_id ?? null,
      orderNumber: layerOrderNumber(layer),
      kind,
      method: methodFromKind(kind),
      date: layer.order_date ?? '',
      sku: layer.bulk_sku,
      product: productForSku(layer.bulk_sku, skuDisplayNames, layer.product),
      invoiceNumber,
      invoiceLinked,
      cases: qty,
      unitCost: Number.isFinite(unitCost) ? unitCost : null,
      destination:
        manual?.store_location?.trim() || destinationForKind(kind),
      detail:
        kind === 'opening'
          ? 'From stores'
          : kind === 'adjustment'
            ? 'Warehouse adjustment'
            : '',
      problem: null,
      warnings,
    })
  }

  for (const entry of manualEntries) {
    if (entry.entry_type !== 'adjustment') continue
    if (entry.adjustment_kind !== 'warehouse_qty') continue
    const cases = Number(entry.cases) || 0
    if (cases >= 0) continue
    rows.push({
      key: manualProgressionKey(entry),
      ledgerTxnId: entry.id ?? null,
      linkableLedgerTxnId: entry.id ?? null,
      linkableSource: 'manual',
      bsLogId: entry.bs_log_id ?? null,
      orderNumber: null,
      kind: 'adjustment',
      method: 'manual',
      date: entry.entry_date,
      sku: entry.bk_sku,
      product: productForSku(entry.bk_sku, skuDisplayNames),
      invoiceNumber: entry.cost_source_invoice?.trim() ?? '',
      invoiceLinked: false,
      cases,
      unitCost: entry.case_cost != null ? Number(entry.case_cost) : null,
      destination: entry.store_location?.trim() || destinationForKind('adjustment'),
      detail: entry.notes?.trim() || 'Warehouse adjustment',
      problem: null,
      warnings: [],
    })
  }

  for (const move of movements) {
    const qty = Number(move.qty) || 0
    const problem = move.exception
      ? move.exception.replace(/^No inventory layers for\b/i, 'No stock for')
      : null
    if (qty === 0 && !problem) continue
    const warnings: string[] = []
    if (problem) warnings.push(problem)
    if ((Number(move.total_cost) || 0) === 0 && !problem && qty !== 0) {
      warnings.push('Shipment recorded at $0 cost')
    }
    const invoiceNumber = whInvoiceForMonth(move.report_month, invoices)
    const orderNumber = (move.wh_order_number ?? '').trim() || null
    rows.push({
      key: movementProgressionKey(move),
      ledgerTxnId: move.id ?? null,
      linkableLedgerTxnId: move.id ?? null,
      linkableSource: 'movement',
      bsLogId: move.bs_log_id ?? null,
      orderNumber,
      kind: 'shipment',
      method: 'shipment',
      date: move.ship_date ?? '',
      sku: move.bulk_sku || move.wh_sku,
      product: productForSku(move.bulk_sku || move.wh_sku, skuDisplayNames),
      invoiceNumber,
      invoiceLinked: Boolean(invoiceNumber),
      cases: qty === 0 ? 0 : -Math.abs(qty),
      unitCost: Number.isFinite(Number(move.unit_cost)) ? Number(move.unit_cost) : null,
      destination: destinationForKind('shipment', move.destination),
      detail: '',
      problem,
      warnings,
    })
  }

  return rows.sort((a, b) => {
    const dateCmp = (a.date || '9999').localeCompare(b.date || '9999')
    if (dateCmp !== 0) return dateCmp
    const order: Record<ProgressionRowKind, number> = {
      opening: 0,
      manufacturer: 1,
      adjustment: 2,
      return: 3,
      shipment: 4,
    }
    return order[a.kind] - order[b.kind] || a.sku.localeCompare(b.sku)
  })
}

export function buildStockRows(
  layers: WarehouseInventoryLayerItem[],
  skuDisplayNames: Map<string, string>
): StockRow[] {
  const bySku = new Map<string, { cases: number; value: number; product: string }>()
  for (const layer of layers) {
    const cases = Number(layer.qty_remaining) || 0
    if (cases <= 0) continue
    const cost = Number(layer.unit_cost) || 0
    const existing = bySku.get(layer.bulk_sku)
    const product = productForSku(layer.bulk_sku, skuDisplayNames, layer.product)
    if (!existing) {
      bySku.set(layer.bulk_sku, { cases, value: cases * cost, product })
    } else {
      existing.cases += cases
      existing.value += cases * cost
      if (!existing.product) existing.product = product
    }
  }
  return [...bySku.entries()]
    .map(([sku, row]) => ({
      sku,
      product: row.product,
      casesLeft: row.cases,
      avgCost: row.cases > 0 ? row.value / row.cases : 0,
    }))
    .sort((a, b) => a.sku.localeCompare(b.sku))
}

export function buildStockRowsFromProgression(
  rows: ProgressionRow[],
  skuDisplayNames: Map<string, string>
): StockRow[] {
  const bySku = new Map<string, { cases: number; value: number; product: string }>()
  for (const row of rows) {
    const sku = row.sku.trim()
    if (!sku) continue
    const cases = Number(row.cases) || 0
    const cost = row.unitCost ?? 0
    const product = row.product.trim() || productForSku(sku, skuDisplayNames)
    const existing = bySku.get(sku)
    if (!existing) {
      bySku.set(sku, { cases, value: cases * cost, product })
    } else {
      existing.cases += cases
      existing.value += cases * cost
      if (!existing.product) existing.product = product
    }
  }
  return [...bySku.entries()]
    .filter(([, row]) => row.cases > 0)
    .map(([sku, row]) => ({
      sku,
      product: row.product,
      casesLeft: row.cases,
      avgCost: row.cases > 0 ? row.value / row.cases : 0,
    }))
    .sort((a, b) => a.sku.localeCompare(b.sku))
}

function defaultCatalogAsOfDate(): string {
  const today = new Date()
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
  return lastDay.toISOString().slice(0, 10)
}

export default function WarehouseInventoryWorkspace({
  availableMonths,
  formatMonthLabel,
  movements,
  invoices,
  bkMatches,
  skuDisplayNames,
  viewMode,
  onViewModeChange,
  catalogSkuFilterRequest,
  ledgerBkTxnFilterRequest,
  onSkuCrosswalkUpdated,
  onSkuCrosswalkSaved,
  busy = false,
  brightSitesAuthRecoveryHint = false,
  onBrightSitesAuthError,
  onBrightSitesApplied,
  onJumpToProductInvoice,
}: WarehouseInventoryWorkspaceProps) {
  const [ledgerRows, setLedgerRows] = useState<EditableLedgerRow[]>([])
  const [ledgerLoading, setLedgerLoading] = useState(true)
  const [savingLedger, setSavingLedger] = useState(false)
  const [catalogAsOf, setCatalogAsOf] = useState(defaultCatalogAsOfDate)
  const [catalogItems, setCatalogItems] = useState<WarehouseLedgerCatalogAsOfItem[]>([])
  const [catalogLoading, setCatalogLoading] = useState(false)
  const ledgerBaselinesRef = useRef<Map<string, LedgerEditableSnapshot>>(new Map())

  const loadLedger = useCallback(async () => {
    setLedgerLoading(true)
    try {
      const response = await listWarehouseLedger({ limit: 2000 })
      const baselines = ledgerBaselinesRef.current
      baselines.clear()
      const rows = response.items.map((item) => {
        const normalized = coerceLedgerMoneyFields(item)
        const row: EditableLedgerRow = {
          ...normalized,
          kind: normalizeLedgerKind(normalized.kind),
          dirty: false,
        }
        baselines.set(row.bk_txn_id, snapshotLedgerEditableFields(row))
        return row
      })
      setLedgerRows(rows)
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setLedgerLoading(false)
    }
  }, [])

  const loadCatalogAsOf = useCallback(async (asOf: string) => {
    setCatalogLoading(true)
    try {
      const response = await getWarehouseLedgerCatalogAsOf(asOf)
      setCatalogItems(response.items)
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setCatalogLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadLedger()
  }, [loadLedger])

  useEffect(() => {
    void loadCatalogAsOf(catalogAsOf)
  }, [catalogAsOf, loadCatalogAsOf])

  async function handleBrightSitesSynced() {
    await loadLedger()
    await loadCatalogAsOf(catalogAsOf)
    await onBrightSitesApplied?.()
  }

  const stockBySku = useMemo(() => {
    const map = new Map<string, StockBySkuEntry>()
    for (const item of catalogItems) {
      const sku = normalizeBulkSku(item.bk_sku)
      if (!sku) continue
      const avgUnitCost = item.avg_unit_cost
      map.set(sku, {
        sku,
        product: skuDisplayNames.get(sku) ?? sku,
        casesLeft: Number(item.signed_cases) || 0,
        avgCost:
          avgUnitCost != null && Number.isFinite(Number(avgUnitCost))
            ? Number(avgUnitCost)
            : null,
      })
    }
    return map
  }, [catalogItems, skuDisplayNames])

  const flowCatalogStock = useMemo((): CatalogStockRow[] => {
    return Array.from(stockBySku.values()).map((entry) => ({
      sku: entry.sku,
      qty: entry.casesLeft,
      value: entry.casesLeft * (entry.avgCost ?? 0),
      product: entry.product,
    }))
  }, [stockBySku])

  const productInvoiceNumberByLineId = useMemo(() => {
    const map = new Map<number, string>()
    for (const invoice of invoices) {
      for (const line of invoice.lines ?? []) {
        if (line.id != null) map.set(line.id, invoice.invoice_number)
      }
    }
    return map
  }, [invoices])

  const skuOptions = useMemo(() => {
    const bySku = new Map<string, string>()
    for (const [sku, displayName] of skuDisplayNames) {
      bySku.set(normalizeBulkSku(sku), displayName)
    }
    for (const row of ledgerRows) {
      const sku = normalizeBulkSku(row.bk_sku)
      if (!sku || bySku.has(sku)) continue
      bySku.set(sku, row.product_name?.trim() || sku)
    }
    return Array.from(bySku.entries())
      .map(([sku, product]) => ({
        sku,
        product,
        label: ledgerProductLabel(sku, product),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
  }, [ledgerRows, skuDisplayNames])

  const problemCount = useMemo(
    () => movements.filter((move) => Boolean(move.exception?.trim())).length,
    [movements]
  )

  const ledgerErrorCount = useMemo(
    () => countLedgerRowsWithRedStatus(ledgerRows),
    [ledgerRows]
  )

  const ledgerErrorLabel =
    ledgerErrorCount === 1
      ? '1 ledger row with errors'
      : `${ledgerErrorCount} ledger rows with errors`

  function updateLedgerRow(bkTxnId: string, patch: Partial<EditableLedgerRow>) {
    setLedgerRows((prev) =>
      prev.map((row) => {
        if (row.bk_txn_id !== bkTxnId) return row
        const next = { ...row, ...patch }
        next.dirty = isLedgerRowDirtyAgainstBaseline(
          next,
          ledgerBaselinesRef.current.get(bkTxnId)
        )
        return next
      })
    )
    if (patch.ignored_flags !== undefined) {
      void useWarehouseProblemStore.getState().fetchLedgerErrorCount()
    }
  }

  async function handleVoidLedgerRow(bkTxnId: string) {
    if (workspaceBusy) return
    try {
      await voidWarehouseLedgerTransaction(bkTxnId)
      // Reload so running balances for the SKU (and peers) refresh.
      await loadLedger()
      await loadCatalogAsOf(catalogAsOf)
      void useWarehouseProblemStore.getState().fetchLedgerErrorCount()
    } catch (err) {
      toast.error(formatApiError(err))
    }
  }

  async function handleUnvoidLedgerRow(bkTxnId: string) {
    if (workspaceBusy) return
    try {
      await unvoidWarehouseLedgerTransaction(bkTxnId)
      await loadLedger()
      await loadCatalogAsOf(catalogAsOf)
      void useWarehouseProblemStore.getState().fetchLedgerErrorCount()
    } catch (err) {
      toast.error(formatApiError(err))
    }
  }

  async function saveDirtyLedgerRows() {
    const dirtyRows = ledgerRows.filter(isSaveableDirtyLedgerTxnRow)
    if (dirtyRows.length === 0 || busy || savingLedger) return
    setSavingLedger(true)
    try {
      for (const row of dirtyRows) {
        const baseline = ledgerBaselinesRef.current.get(row.bk_txn_id)
        if (!baseline) continue
        const patch = ledgerRowToPatch(row, baseline)
        if (Object.keys(patch).length === 0) continue
        const updated = await patchWarehouseLedgerTransaction(row.bk_txn_id, patch)
        ledgerBaselinesRef.current.set(
          updated.bk_txn_id,
          snapshotLedgerEditableFields(updated)
        )
        setLedgerRows((prev) =>
          prev.map((item) =>
            item.bk_txn_id === updated.bk_txn_id
              ? { ...updated, dirty: false }
              : item
          )
        )
      }
      toast.success(
        `Saved ${dirtyRows.length} ledger change${dirtyRows.length === 1 ? '' : 's'}.`
      )
      await loadLedger()
      await loadCatalogAsOf(catalogAsOf)
      void useWarehouseProblemStore.getState().fetchLedgerErrorCount()
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setSavingLedger(false)
    }
  }

  const dirtyLedgerCount = ledgerRows.filter(isSaveableDirtyLedgerTxnRow).length
  const workspaceBusy = busy || ledgerLoading || savingLedger

  return (
    <div
      className={
        viewMode === 'catalog' || viewMode === 'ledger' || viewMode === 'flow'
          ? 'flex min-h-0 flex-1 flex-col gap-4'
          : 'space-y-4'
      }
    >
      <UnderlineTabNav
        className="shrink-0"
        ariaLabel="Inventory views"
        tabs={[
          {
            id: 'ledger',
            label: 'Ledger',
            badge:
              ledgerErrorCount > 0 ? (
                <span
                  className="h-2 w-2 shrink-0 rounded-full bg-red-500"
                  aria-label={ledgerErrorLabel}
                  title={ledgerErrorLabel}
                />
              ) : undefined,
          },
          {
            id: 'flow',
            label: 'Flow chart',
            badge:
              problemCount > 0 ? (
                <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-800">
                  {problemCount}
                </span>
              ) : undefined,
          },
          { id: 'catalog', label: 'Catalog' },
          { id: 'vendors', label: 'Vendors' },
        ]}
        activeId={viewMode}
        onChange={(id) => onViewModeChange(id as InventoryViewMode)}
      />

      {viewMode === 'ledger' ? (
        <div className="flex min-h-0 flex-1 flex-col rounded-xl border border-gray-200 bg-white p-4 pb-6 md:p-5 md:pb-8">
          <div className="flex shrink-0 flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="container-title">Inventory ledger</h2>
              <p className="mt-1 text-xs text-gray-500">
                Persisted inventory transactions from Bright Sites and manual edits. Field overrides
                are preserved on re-sync; rows with upstream drift are marked Upstream changed.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <WarehouseBrightSitesLedgerSync
                busy={workspaceBusy}
                authRecoveryHint={brightSitesAuthRecoveryHint}
                onAuthError={onBrightSitesAuthError}
                onSynced={handleBrightSitesSynced}
              />
              {dirtyLedgerCount > 0 ? (
                <button
                  type="button"
                  className="btn btn-primary text-sm"
                  disabled={workspaceBusy}
                  onClick={() => void saveDirtyLedgerRows()}
                >
                  {savingLedger
                    ? 'Saving…'
                    : `Save ${dirtyLedgerCount} ledger change${dirtyLedgerCount === 1 ? '' : 's'}`}
                </button>
              ) : null}
            </div>
          </div>
          <WarehouseLedgerTable
            rows={ledgerRows}
            skuOptions={skuOptions}
            skuDisplayNames={skuDisplayNames}
            busy={workspaceBusy}
            invoiceNumberByLineId={productInvoiceNumberByLineId}
            bkTxnFilterRequest={ledgerBkTxnFilterRequest}
            onUpdateRow={updateLedgerRow}
            onJumpToProductInvoice={onJumpToProductInvoice}
            onVoidRow={(bkTxnId) => void handleVoidLedgerRow(bkTxnId)}
            onUnvoidRow={(bkTxnId) => void handleUnvoidLedgerRow(bkTxnId)}
          />
        </div>
      ) : null}

      {viewMode === 'flow' ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white p-4 md:p-5">
          <div className="shrink-0">
            <h2 className="container-title">Flow chart</h2>
            <p className="mt-1 text-xs text-gray-500">
              Per-SKU waterfall of opening stock, purchases, store draws, and ending inventory.
              Switch to Sankey for purchases through the warehouse to stores.
            </p>
          </div>
          <div className="mt-4 flex min-h-0 flex-1 flex-col">
            <WarehouseInventoryFlow
              ledgerRows={ledgerRows}
              catalogStock={flowCatalogStock}
              invoices={invoices}
              bkMatches={bkMatches}
              availableMonths={availableMonths}
              formatMonthLabel={formatMonthLabel}
              skuDisplayNames={skuDisplayNames}
              productInvoiceNumberByLineId={productInvoiceNumberByLineId}
            />
          </div>
        </div>
      ) : null}

      {viewMode === 'catalog' ? (
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          <WarehouseSkuCrosswalkCard
            onUpdated={onSkuCrosswalkUpdated}
            onSaved={onSkuCrosswalkSaved}
            stockBySku={stockBySku}
            catalogAsOf={catalogAsOf}
            onCatalogAsOfChange={setCatalogAsOf}
            catalogLoading={catalogLoading}
            busy={busy || catalogLoading}
            catalogSkuFilterRequest={catalogSkuFilterRequest}
            brightSitesAuthRecoveryHint={brightSitesAuthRecoveryHint}
            onBrightSitesAuthError={onBrightSitesAuthError}
            onBrightSitesApplied={handleBrightSitesSynced}
          />
        </div>
      ) : null}

      {viewMode === 'vendors' ? (
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          <WarehouseVendorsCard busy={busy} />
        </div>
      ) : null}
    </div>
  )
}
